/**
 * formSchema.ts — the seller dynamic-attribute contract layer (PIM Wave P2,
 * Task 1).
 *
 * Mirrors the backend's frozen `GET /marketplace/categories/{id}/form-schema`
 * response (`apps/api/garden/features/catalog_schema/schemas.py::
 * FormSchemaResponse`) and the attribute-value wire DTOs (`AttributeValueIn`/
 * `AttributeValueOut`), plus a RUNTIME Zod schema builder that reproduces the
 * per-`field_type` value rules from `service.py`'s `_validate_*` functions.
 *
 * Design notes (recon-derived — see `docs/backlog-health/pim-wave-build-log.md`
 * P2 Task 0):
 *   - The seven field types are `text | number | boolean | select | size |
 *     date | image`.
 *   - Form-value representation is a FLAT `Record<string, string>` keyed by
 *     `field_slug`, so React Hook Form registration stays first-order and
 *     strict-safe. A `size` field uses TWO keys — the number string at
 *     `field_slug` and its unit at `unitKey(field_slug)` (`${slug}::unit`).
 *     A `boolean` field is encoded as the tri-state string
 *     `"unanswered" | "true" | "false"` — faithfully mirroring the backend's
 *     "no stored value row = unanswered" law (`_binding_is_visible` +
 *     `compute_completeness`), so an untouched boolean is genuinely absent,
 *     never "answered false" (this resolves the P1 build log's owed
 *     "unchecked-checkbox no-row-vs-false" decision).
 *   - The Zod builder validates PRESENT-value FORMAT only. "Empty = absent"
 *     is always allowed at the field level; REQUIRED-missing is enforced by
 *     the server submit gate (`pim.required_missing`) and surfaced client-side
 *     as the disabled-with-reason submit affordance (Task 7/9), NOT here.
 *     This matches the backend split: `apply_attribute_values` validates every
 *     present value on EVERY write (draft or submit) → `pim.attribute_invalid`;
 *     the gate separately checks required only when the resulting status is
 *     `pending_approval`/`active` → `pim.required_missing`.
 *   - Visibility is deliberately NOT a concern of this file. Hidden fields are
 *     excluded from validation + payload by CLEARING their value to `""`
 *     (Task 4's `DynamicAttributeForm` owns that), which this file already
 *     treats as absent. `visibility.ts` (Task 2) owns the pure evaluator.
 *
 * Q4 (locked spec): the seller's RAW value (+ raw unit) is what round-trips
 * and displays; the backend derives the standardized value. This layer never
 * standardizes — it ships exactly what the seller typed.
 */
import { z } from "zod";

// ─────────────────────────── Frozen enums ──────────────────────────────────

/** The seven field types (frozen — spec Q13 / `FIELD_TYPE_VALUES`). */
export const FIELD_TYPES = [
  "text",
  "number",
  "boolean",
  "select",
  "size",
  "date",
  "image",
] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

/** Binding requirement (frozen — `BINDING_REQUIREMENT_VALUES`). */
export type Requirement = "required" | "recommended" | "optional";

/** Visibility operators (frozen — `_VISIBILITY_OPERATORS`). */
export type VisibilityOperator = "eq" | "in";

// ─────────────────────────── Wire types (mirror the API) ───────────────────

/**
 * One RU/KY/EN triple (`TrilingualText`). `ru` is always present; `ky`/`en`
 * pass through verbatim (`null` included) — the CLIENT owns the RU fallback
 * (see `pickText` below).
 */
export interface TrilingualText {
  ru: string;
  ky: string | null;
  en: string | null;
}

/** A controlling value a `visibility_rule` compares against — a slug/scalar or
 * (for `in`) a list of them. */
export type VisibilityValue = string | number | boolean;

/** A binding's conditional-visibility rule (`{field_slug, operator, value}`). */
export interface VisibilityRule {
  field_slug: string;
  operator: VisibilityOperator;
  value: VisibilityValue | VisibilityValue[];
}

/** One surviving (non-archived) dropdown option for a `select` field. */
export interface FormFieldOption {
  value_slug: string;
  label: TrilingualText;
  sort_order: number;
}

/** One resolved, policy-filtered field on the form contract (`FormFieldDto`). */
export interface FormField {
  field_slug: string;
  field_type: FieldType;
  requirement: Requirement;
  label: TrilingualText;
  hint: TrilingualText | null;
  example: TrilingualText | null;
  sort_order: number;
  visibility_rule: VisibilityRule | null;
  /** `size` only. */
  unit_kind: string | null;
  /** `size` only — the canonical unit conversions target. */
  canonical_unit: string | null;
  /** `size` only — the units the seller may pick from. */
  allowed_units: string[] | null;
  /** `select` only. */
  options: FormFieldOption[] | null;
}

/** One `attribute_field_groups` row, trilingual-named (`FormGroupDto`). */
export interface FormGroup {
  id: string;
  slug: string;
  name: TrilingualText;
  sort_order: number;
}

/** One rendered section: a group (`null` = the ungrouped bucket, always last)
 * plus its ordered fields (`FormSchemaSection`). */
export interface FormSchemaSection {
  group: FormGroup | null;
  fields: FormField[];
}

/** The single form contract for a category (`FormSchemaResponse`). */
export interface FormSchemaResponse {
  category_id: string;
  category_slug: string;
  category_name: TrilingualText;
  groups: FormSchemaSection[];
}

/**
 * One attribute-value WRITE (`AttributeValueIn`). `value` is whatever JSON
 * scalar the field type calls for; `unit` is meaningful only for `size`.
 * Number/size values ride the wire as decimal STRINGS (the backend's
 * `_decimal_from_number_like` coerces `Decimal(str(value))`), mirroring how
 * `price_kgs` is sent.
 */
export interface AttributeValueIn {
  field_slug: string;
  value: string | number | boolean | null;
  unit?: string | null;
}

/**
 * One stored attribute value on a read path (`AttributeValueOut`). `value` is
 * type-rendered by the backend: `select` → the option's `value_slug`; `date`
 * → an ISO string; `number`/`size` → the raw `Decimal` as a JSON string;
 * `boolean` → a JSON boolean; `image` → the upload reference. `unit`/
 * `value_std` are populated only for `size`; `option_label` only for `select`.
 */
export interface AttributeValueOut {
  field_slug: string;
  field_type: FieldType;
  value: string | number | boolean | null;
  unit: string | null;
  value_std: string | number | null;
  label: TrilingualText;
  option_label: TrilingualText | null;
}

// ─────────────────────────── Trilingual rendering ──────────────────────────

type Lang = "ru" | "ky" | "en";

function isBlank(text: string | null | undefined): boolean {
  return text === null || text === undefined || text.trim() === "";
}

/**
 * Chosen-language text with RU fallback. `TrilingualText.ru` is always
 * present; `ky`/`en` fall back to `ru` whenever null/blank. Mirrors the admin
 * `FormPreview.pickText` exactly so seller + admin render identically.
 */
export function pickText(text: TrilingualText, language: string): string {
  const lang = normaliseLang(language);
  if (lang === "ky" && !isBlank(text.ky)) return text.ky as string;
  if (lang === "en" && !isBlank(text.en)) return text.en as string;
  return text.ru;
}

/** Same fallback, but the WHOLE object may be `null` (hint/example — the
 * backend nulls the object when RU was never authored). */
export function pickOptionalText(
  text: TrilingualText | null,
  language: string,
): string | undefined {
  if (!text) return undefined;
  return pickText(text, language);
}

function normaliseLang(language: string): Lang {
  if (language.startsWith("ky")) return "ky";
  if (language.startsWith("en")) return "en";
  return "ru";
}

// ─────────────────────────── Form-value model ──────────────────────────────

/**
 * Flat RHF value map keyed by `field_slug` (+ `unitKey(slug)` for `size`).
 * Every value is a string; booleans use the tri-state encoding below.
 */
export type AttributeFormValues = Record<string, string>;

/** The RHF key holding a `size` field's unit (its number lives at the slug). */
export function unitKey(fieldSlug: string): string {
  return `${fieldSlug}::unit`;
}

/** Boolean tri-state form encoding (mirrors the backend "no row = unanswered"). */
export const BOOL_UNANSWERED = "unanswered";
export const BOOL_TRUE = "true";
export const BOOL_FALSE = "false";

/** `number`/`size` magnitude bound (inclusive) — backend `_NUMBER_BOUND`. */
export const NUMBER_BOUND = 1_000_000_000_000; // ±10^12
const TEXT_MAX_LEN = 2000;

/**
 * i18n message keys for value errors — used by BOTH the Zod builder (client
 * format checks) and the 422→field mapper (server `pim.*` codes, Task 6), so
 * a client-side and a server-side rejection of the same field read identically.
 * All resolve under the `seller-dashboard` namespace's `attributes.errors.*`.
 */
export const ATTR_ERROR_KEYS = {
  typeMismatch: "attributes.errors.typeMismatch",
  tooLong: "attributes.errors.tooLong",
  number: "attributes.errors.number",
  selectInvalid: "attributes.errors.selectInvalid",
  unitMissing: "attributes.errors.unitMissing",
  unitInvalid: "attributes.errors.unitInvalid",
  date: "attributes.errors.date",
  image: "attributes.errors.image",
  required: "attributes.errors.required",
  generic: "attributes.errors.generic",
} as const;

/**
 * Map a backend `pim.*` per-field code (from the 422 `fields[]` array) to the
 * matching client message key. Any unmapped/unknown code → `generic`.
 */
export function pimCodeToMessageKey(code: string): string {
  switch (code) {
    case "pim.type_mismatch":
      return ATTR_ERROR_KEYS.typeMismatch;
    case "pim.option_unknown":
    case "pim.option_archived":
      return ATTR_ERROR_KEYS.selectInvalid;
    case "pim.unit_missing":
      return ATTR_ERROR_KEYS.unitMissing;
    case "pim.unit_unknown":
    case "pim.unit_not_allowed":
      return ATTR_ERROR_KEYS.unitInvalid;
    case "pim.upload_missing":
    case "pim.upload_infected":
      return ATTR_ERROR_KEYS.image;
    case "pim.required_missing":
      return ATTR_ERROR_KEYS.required;
    default:
      return ATTR_ERROR_KEYS.generic;
  }
}

// ─────────────────────────── Presence + number helpers ─────────────────────

/** True when a raw form string is "present" (non-blank after trim). */
export function isPresent(raw: string | undefined): boolean {
  return raw !== undefined && raw.trim() !== "";
}

/**
 * Client mirror of the backend `_decimal_from_number_like`: a finite number
 * within ±10^12 (bool/NaN/Inf/out-of-range rejected). Returns the parsed
 * number or `null`. Numeric strings, decimals, signs, and scientific notation
 * all pass (`Number(...)`); the server (which uses `Decimal`) remains the
 * authority for the few edge forms `Number` accepts that `Decimal` doesn't.
 */
export function parseNumberLike(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  if (n < -NUMBER_BOUND || n > NUMBER_BOUND) return null;
  return n;
}

/**
 * Is this field's current form value "filled" (a value row would exist)? Used
 * by completeness + the required-missing gate (Task 7/9). Mirrors the backend
 * "filled = a `product_attribute_values` row exists" — content is irrelevant,
 * only presence. A `size` field is filled iff its NUMBER part is present (the
 * unit alone never makes a row).
 */
export function isFieldFilled(field: FormField, values: AttributeFormValues): boolean {
  const raw = values[field.field_slug];
  if (field.field_type === "boolean") {
    return raw === BOOL_TRUE || raw === BOOL_FALSE;
  }
  return isPresent(raw);
}

// ─────────────────────────── Runtime Zod builder ───────────────────────────

/**
 * Build a runtime Zod schema validating the PRESENT-value FORMAT of every
 * field in `schema`, reproducing the backend `_validate_*` rules. The shape is
 * `Record<key, z.string()>` (every field + every size unit key) and a single
 * `.superRefine` does the per-type checks so error `path`s land on the exact
 * RHF key (`field_slug`, or `unitKey(field_slug)` for a `size` unit issue).
 *
 * Empty/absent always passes (required-missing is the submit gate's job). The
 * caller clears hidden fields to `""` (Task 4), so hidden fields validate as
 * absent — i.e. "hidden fields excluded from validation" falls out for free.
 */
export function buildAttributesSchema(schema: FormSchemaResponse) {
  const fields = flattenFields(schema);
  const shape: Record<string, z.ZodString> = {};
  for (const field of fields) {
    shape[field.field_slug] = z.string();
    if (field.field_type === "size") {
      shape[unitKey(field.field_slug)] = z.string();
    }
  }

  const base = z.object(shape).partial();

  return base.superRefine((values: Record<string, string | undefined>, ctx) => {
    for (const field of fields) {
      const raw = values[field.field_slug];
      const slug = field.field_slug;

      switch (field.field_type) {
        case "text": {
          if (!isPresent(raw)) break;
          if ((raw as string).trim().length > TEXT_MAX_LEN) {
            addIssue(ctx, slug, ATTR_ERROR_KEYS.tooLong);
          }
          break;
        }
        case "number": {
          if (!isPresent(raw)) break;
          if (parseNumberLike(raw as string) === null) {
            addIssue(ctx, slug, ATTR_ERROR_KEYS.number);
          }
          break;
        }
        case "boolean": {
          // Encoded tri-state; any of the three sentinels is valid, and an
          // out-of-band value can only arise from a programming error.
          if (
            raw !== undefined &&
            raw !== BOOL_UNANSWERED &&
            raw !== BOOL_TRUE &&
            raw !== BOOL_FALSE &&
            raw !== ""
          ) {
            addIssue(ctx, slug, ATTR_ERROR_KEYS.typeMismatch);
          }
          break;
        }
        case "select": {
          if (!isPresent(raw)) break;
          const slugs = new Set((field.options ?? []).map((o) => o.value_slug));
          if (!slugs.has(raw as string)) {
            addIssue(ctx, slug, ATTR_ERROR_KEYS.selectInvalid);
          }
          break;
        }
        case "size": {
          if (!isPresent(raw)) break; // number absent → whole field absent
          if (parseNumberLike(raw as string) === null) {
            addIssue(ctx, slug, ATTR_ERROR_KEYS.number);
          }
          const unit = values[unitKey(slug)];
          if (!isPresent(unit)) {
            addIssue(ctx, unitKey(slug), ATTR_ERROR_KEYS.unitMissing);
          } else if (!(field.allowed_units ?? []).includes(unit as string)) {
            addIssue(ctx, unitKey(slug), ATTR_ERROR_KEYS.unitInvalid);
          }
          break;
        }
        case "date": {
          if (!isPresent(raw)) break;
          if (!isIsoDate(raw as string)) {
            addIssue(ctx, slug, ATTR_ERROR_KEYS.date);
          }
          break;
        }
        case "image": {
          // Presence-only client check (an upload id); the backend validates
          // existence + ownership + clean scan.
          break;
        }
      }
    }
  });
}

function addIssue(
  ctx: z.RefinementCtx,
  path: string,
  messageKey: string,
): void {
  ctx.addIssue({ code: z.ZodIssueCode.custom, message: messageKey, path: [path] });
}

/** A YYYY-MM-DD string a native `<input type="date">` yields (and the backend
 * `date.fromisoformat` accepts). */
function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return false;
  const d = new Date(`${value.trim()}T00:00:00Z`);
  return !Number.isNaN(d.getTime());
}

// ─────────────────────────── Schema helpers ────────────────────────────────

/** Every group's fields (ungrouped bucket included) as one ordered list —
 * used for global controller lookup + total/hidden counts. Mirrors the admin
 * `FormPreview.flattenFields`. */
export function flattenFields(schema: FormSchemaResponse): FormField[] {
  return schema.groups.flatMap((section) => section.fields);
}

/**
 * Initial RHF values for a category's schema, hydrated from any existing
 * stored `attributes` (edit mode). Every field gets a key so RHF owns it;
 * unfilled fields default to `""` (booleans to `"unanswered"`).
 */
export function initialFormValues(
  schema: FormSchemaResponse,
  existing: readonly AttributeValueOut[] = [],
): AttributeFormValues {
  const bySlug = new Map(existing.map((v) => [v.field_slug, v]));
  const out: AttributeFormValues = {};
  for (const field of flattenFields(schema)) {
    const stored = bySlug.get(field.field_slug);
    switch (field.field_type) {
      case "boolean":
        out[field.field_slug] =
          stored?.value === true
            ? BOOL_TRUE
            : stored?.value === false
              ? BOOL_FALSE
              : BOOL_UNANSWERED;
        break;
      case "size":
        out[field.field_slug] = stored != null ? stringifyValue(stored.value) : "";
        out[unitKey(field.field_slug)] = stored?.unit ?? "";
        break;
      default:
        out[field.field_slug] = stored != null ? stringifyValue(stored.value) : "";
        break;
    }
  }
  return out;
}

function stringifyValue(value: string | number | boolean | null): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

/**
 * When the seller changes the PRIMARY category, the new category's schema is a
 * different field set. Carry forward any entered value whose `field_slug`
 * survives into the new schema (fields recur across categories by slug — spec
 * Q6: slug is immutable identity), and report which previously-FILLED slugs are
 * being DROPPED (present in the old form, absent from the new schema) so the
 * editor can warn before it commits the switch.
 *
 * `previousValues` is the outgoing flat form values (all keys, incl. size unit
 * keys). Unit keys (`${slug}::unit`) are not fields and never count as dropped.
 */
export function carryOverValues(
  previousValues: AttributeFormValues,
  newSchema: FormSchemaResponse,
): { initialValues: AttributeFormValues; droppedSlugs: string[] } {
  const newFields = flattenFields(newSchema);
  const newSlugs = new Set(newFields.map((f) => f.field_slug));

  const initial: AttributeFormValues = {};
  for (const field of newFields) {
    const prev = previousValues[field.field_slug];
    if (field.field_type === "boolean") {
      initial[field.field_slug] =
        prev === BOOL_TRUE || prev === BOOL_FALSE ? prev : BOOL_UNANSWERED;
    } else if (field.field_type === "size") {
      initial[field.field_slug] = prev ?? "";
      initial[unitKey(field.field_slug)] = previousValues[unitKey(field.field_slug)] ?? "";
    } else {
      initial[field.field_slug] = prev ?? "";
    }
  }

  const droppedSlugs: string[] = [];
  for (const [key, val] of Object.entries(previousValues)) {
    if (key.endsWith("::unit")) continue; // a size unit companion, not a field
    if (newSlugs.has(key)) continue; // survives → not dropped
    if (val === undefined || val === "" || val === BOOL_UNANSWERED) continue; // not filled
    droppedSlugs.push(key);
  }
  return { initialValues: initial, droppedSlugs };
}

/**
 * Build the `attributes` write payload from the current form values, INCLUDING
 * only fields whose slug is in `visibleSlugs` and whose value is present.
 * Hidden fields are excluded from the payload (spec/Task 2); because the write
 * is full-replace-when-present, an excluded field's stored row is dropped —
 * matching the backend's "a hidden field's value is inert" semantics.
 */
export function toAttributePayload(
  schema: FormSchemaResponse,
  values: AttributeFormValues,
  visibleSlugs: ReadonlySet<string>,
): AttributeValueIn[] {
  const payload: AttributeValueIn[] = [];
  for (const field of flattenFields(schema)) {
    if (!visibleSlugs.has(field.field_slug)) continue;
    const raw = values[field.field_slug];

    switch (field.field_type) {
      case "boolean": {
        if (raw === BOOL_TRUE) payload.push({ field_slug: field.field_slug, value: true });
        else if (raw === BOOL_FALSE) payload.push({ field_slug: field.field_slug, value: false });
        // unanswered → omit (no row)
        break;
      }
      case "size": {
        if (!isPresent(raw)) break;
        payload.push({
          field_slug: field.field_slug,
          value: (raw as string).trim(),
          unit: values[unitKey(field.field_slug)]?.trim() || null,
        });
        break;
      }
      case "number": {
        if (!isPresent(raw)) break;
        payload.push({ field_slug: field.field_slug, value: (raw as string).trim() });
        break;
      }
      default: {
        // text | select | date | image — send the trimmed raw string.
        if (!isPresent(raw)) break;
        payload.push({ field_slug: field.field_slug, value: (raw as string).trim() });
        break;
      }
    }
  }
  return payload;
}
